/**
 * BESTIX v5.0 — Magazine Landing Page
 * Editorial content hub
 */

import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Hero } from '@/components/hero/Hero';
import { ArticleCard } from '@/components/magazine';
import { SectionHeader } from '@/components/ui';
import { articles, getFeaturedArticles } from '@/services/magazineData';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { defaultHero } from '@/config/hero-images';

export default function MagazinePage() {
  const featuredArticle = getFeaturedArticles()[0];
  const restArticles = articles.filter(a => !a.featured);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <Header variant="transparent" />

      <main>
        {/* Hero Section */}
        <div className="relative -mt-20 pt-20">
          <Hero
            title="The Magazine"
            subtitle="By BESTIX"
            backgroundImage={defaultHero}
            searchLabel="EXPLORE STORIES"
            searchPlaceholder="Search articles, topics, guides..."
            showScrollIndicator={true}
          />
        </div>

        {/* Category Nav */}
        <nav className="border-b border-white/10 bg-[var(--color-navy)]">
          <div className="max-w-6xl mx-auto px-6">
            <ul className="flex items-center gap-1 overflow-x-auto py-4 -mx-2 scrollbar-hide">
              {[
                { name: 'All', slug: '' },
                { name: 'Manifesto', slug: 'manifesto' },
                { name: 'Event Types', slug: 'archetypes' },
                { name: 'Ticket Tips', slug: 'ticket-confidence' },
                { name: 'Seat Guide', slug: 'seat-snob' },
                { name: 'Venues', slug: 'venue-intel' },
                { name: 'City Nights', slug: 'city-nights' },
                { name: 'Why Go', slug: 'why-go' },
                { name: 'Best Of', slug: 'best-of' },
                { name: 'Culture', slug: 'culture' },
              ].map((cat) => (
                <li key={cat.slug}>
                  <Link
                    to={cat.slug ? `/magazine/category/${cat.slug}` : '/magazine'}
                    className={cn(
                      'px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors',
                      'text-white/70 hover:text-white hover:bg-white/10'
                    )}
                  >
                    {cat.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </nav>

        <div className="max-w-6xl mx-auto px-6 py-12">
          {/* Featured Article */}
          {featuredArticle && (
            <section className="mb-16">
              <ArticleCard {...featuredArticle} variant="hero" />
            </section>
          )}

          {/* Latest Articles */}
          <SectionHeader
            title="Latest Stories"
            subtitle="Fresh perspectives on live events"
          />

          <section className="mt-8">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {restArticles.map((article) => (
                <ArticleCard key={article.slug} {...article} />
              ))}
            </div>
          </section>

          {/* Newsletter CTA */}
          <section className="mt-16 bg-[var(--color-navy)] rounded-xl p-8 md:p-12 text-center">
            <h3 className="font-playfair italic text-2xl md:text-3xl text-white !text-white mb-4">
              Never Miss a Story
            </h3>
            <p className="text-white/70 mb-6 max-w-md mx-auto">
              Get our weekly roundup of the best takes on live events, ticket strategies, and insider guides.
            </p>
            <form className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-4 py-3 rounded-full bg-white/10 border border-white/20 text-white placeholder:text-white/50 focus:outline-none focus:border-[var(--color-gold)]"
              />
              <button
                type="submit"
                className="px-6 py-3 bg-[var(--color-gold)] text-[var(--color-navy)] rounded-full font-medium hover:bg-[var(--color-gold-light)] transition-colors"
              >
                Subscribe
              </button>
            </form>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}
